import React, {
  forwardRef,
  useRef,
  useEffect,
  useLayoutEffect,
  useImperativeHandle,
} from "react";
import * as fabric from "fabric";
import useRecorderContext from "../context/useRecorderContext";
import { upgradeFabricBackstoreToQuality } from "../utils/fabricCanvasQuality";
import { RECORDING_FLAGS } from "../constants/recordingFlags";

export interface FabricCanvasProps {
  width: number;  // CSS display width
  height: number; // CSS display height
  onReady?: (canvas: fabric.Canvas) => void;
  onObjectSelected?: (target: fabric.Object | null) => void;
  onObjectModified?: (target: fabric.Object) => void;
  children?: React.ReactNode;
  className?: string;
}

export interface FabricCanvasRef {
  getCanvas: () => fabric.Canvas | null;
  requestRender: () => void;
  requestSmoothRender: () => void;
  addObject: (object: fabric.Object) => void;
  removeObject: (object: fabric.Object) => void;
  clear: () => void;
}

const FabricCanvas = forwardRef<FabricCanvasRef, FabricCanvasProps>(
  (
    { width, height, onReady, onObjectSelected, onObjectModified, className },
    ref
  ) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const fabricCanvasRef = useRef<fabric.Canvas | null>(null);
    const renderRequestRef = useRef<number | null>(null);
    const { recordingQuality, canvasRatio } = useRecorderContext();

    // Store initial dimensions/quality/ratio in refs to use during initialization only
    const initialWidthRef = useRef(width);
    const initialHeightRef = useRef(height);
    const initialQualityRef = useRef(recordingQuality);
    const initialRatioRef = useRef(canvasRatio);

    // Initialize Fabric.js canvas ONLY ONCE on mount
    // All subsequent dimension/quality/ratio changes are handled by the useLayoutEffect below
    // This prevents expensive canvas recreation when switching quality
    useEffect(() => {
      if (!canvasRef.current) {
        return;
      }

      // Create Fabric.js canvas with INITIAL dimensions (from refs, not props)
      // This ensures the effect doesn't re-run when props change
      const canvas = new fabric.Canvas(canvasRef.current, {
        width: initialWidthRef.current,
        height: initialHeightRef.current,
        backgroundColor: "transparent",
        preserveObjectStacking: true,
        renderOnAddRemove: true,
        selection: true, // Enable selection for resize handles
        targetFindTolerance: 4, // Make it easier to select objects
        stopContextMenu: true, // Prevent right-click menu
        fireRightClick: true, // Enable right-click events
        fireMiddleClick: true,
        // Disable multiple selection - no altSelectionKey and selection key is null
        altSelectionKey: null, // Disable keyboard-based multiple selection
        selectionKey: null, // Disable area drag selection for multiple objects
        skipOffscreen: RECORDING_FLAGS.SKIP_OFFSCREEN,
        enableRetinaScaling: false, // Better quality rendering (cause main thread block on 2K)
      });

      // Enable selection but prevent multiple selection
      canvas.selection = true;

      // Handle selection events to ensure single selection
      canvas.on("selection:created", (e: any) => {
        if (e.selected && e.selected.length > 1) {
          // If multiple objects are selected, keep only the first one
          canvas.setActiveObject(e.selected[0]);
        }
        onObjectSelected?.(e.selected?.[0] || null);
      });

      canvas.on("selection:updated", (e: any) => {
        if (e.selected && e.selected.length > 1) {
          // If multiple objects are selected, keep only the first one
          canvas.setActiveObject(e.selected[0]);
        }
        onObjectSelected?.(e.selected?.[0] || null);
      });

      // Clear selection when clicking on empty space
      canvas.on("mouse:down", (options) => {
        if (!options.target) {
          // If clicking on empty space, deselect all
          canvas.discardActiveObject();
          onObjectSelected?.(null);
        }
      });

      // Prevent multiple selection by always clearing previous selection when setting new active object
      const originalSetActiveObject = canvas.setActiveObject;
      canvas.setActiveObject = function (object: fabric.Object) {
        canvas.discardActiveObject();
        return originalSetActiveObject.call(this, object);
      };

      // Upgrade canvas backing store to recording quality (use initial values from refs)
      upgradeFabricBackstoreToQuality(canvas, initialQualityRef.current, initialRatioRef.current, width, height);

      // CRITICAL FIX: Completely bypass Fabric.js's pointer calculation
      // Fabric.js clones _offset before calling the method, so we can't fix it by updating _offset
      // Instead, we compute coordinates directly from getBoundingClientRect
      (canvas as any).getPointer = function(e: any, ignoreZoom?: boolean) {
        const upperCanvas = this.upperCanvasEl;
        if (!upperCanvas) {
          return new fabric.Point(0, 0);
        }
        
        // Get actual canvas position from DOM
        const rect = upperCanvas.getBoundingClientRect();
        
        // Calculate relative position within canvas element
        const clientX = (e as MouseEvent).clientX ?? (e as TouchEvent).touches?.[0]?.clientX ?? 0;
        const clientY = (e as MouseEvent).clientY ?? (e as TouchEvent).touches?.[0]?.clientY ?? 0;
        const relativeX = clientX - rect.left;
        const relativeY = clientY - rect.top;
        
        // Scale from CSS coordinates to backing store coordinates
        const scaleX = this.width / rect.width;
        const scaleY = this.height / rect.height;
        let x = relativeX * scaleX;
        let y = relativeY * scaleY;
        
        // Apply viewportTransform if not ignoring zoom
        if (!ignoreZoom) {
          const vpt = this.viewportTransform;
          if (vpt) {
            // Inverse transform: subtract translation, then divide by scale
            x = (x - vpt[4]) / vpt[0];
            y = (y - vpt[5]) / vpt[3];
          }
        }
        
        return new fabric.Point(x, y);
      };

      fabricCanvasRef.current = canvas;

      // Selection event handlers are already set up above with multiple selection prevention
      // The selection:cleared event is handled in the mouse:down handler

      canvas.on("object:modified", (e: any) => {
        onObjectModified?.(e.target);
      });

      onReady?.(canvas);

      // Handle window resize to recalculate canvas offset for accurate hit detection
      const handleResize = () => {
        if (fabricCanvasRef.current) {
          const c = fabricCanvasRef.current as any;
          if (typeof c.calcOffset === "function") {
            c.calcOffset();
          }
          // Update all object coordinates after resize
          c.forEachObject((obj: fabric.Object) => obj.setCoords());
          c.requestRenderAll();
        }
      };
      window.addEventListener("resize", handleResize);

      return () => {
        window.removeEventListener("resize", handleResize);
        canvas.dispose();
        fabricCanvasRef.current = null;
      };
    // NOTE: This effect runs ONLY on mount (empty deps array equivalent via stable callbacks)
    // All dimension/quality/ratio changes are handled by useLayoutEffect below
    // to avoid expensive canvas recreation when switching quality
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onReady, onObjectSelected, onObjectModified]);

    // Throttled rendering with requestRenderAll - optimized to prevent excessive renders
    const requestRender = React.useCallback(() => {
      if (renderRequestRef.current) {
        return; // Already a render scheduled, no need to schedule another
      }

      renderRequestRef.current = requestAnimationFrame(() => {
        const canvas = fabricCanvasRef.current;
        if (canvas) {
          // Use requestRenderAll for better performance during animations
          canvas.requestRenderAll();
        }
        renderRequestRef.current = null;
      });
    }, []);

    // Optimized render function for smooth animations
    const requestSmoothRender = React.useCallback(() => {
      const canvas = fabricCanvasRef.current;
      if (!canvas) return;

      if (RECORDING_FLAGS.OPTIMIZED_CACHING) {
        // New behavior: Simply request render without toggling objectCaching
        // Objects are marked dirty at mutation time (in video hooks, property setters)
        // FabricJS handles caching automatically - this avoids the cache invalidation storm
        canvas.requestRenderAll();
      } else {
        // Original behavior (fallback) - kept for zero-regression safety
        // Disable object caching during animations for smoother rendering
        const objects = canvas.getObjects();
        objects.forEach((obj: any) => {
          if (obj.objectCaching !== false) {
            obj.objectCaching = false;
          }
        });

        canvas.requestRenderAll();

        // Re-enable object caching after animation
        requestAnimationFrame(() => {
          objects.forEach((obj: any) => {
            if (obj.objectCaching !== true) {
              obj.objectCaching = true;
            }
          });
        });
      }
    }, []);

    // Expose methods via ref
    useImperativeHandle(
      ref,
      () => ({
        getCanvas: () => fabricCanvasRef.current,
        requestRender,
        requestSmoothRender,
        addObject: (object: fabric.Object) => {
          fabricCanvasRef.current?.add(object);
          requestRender();
        },
        removeObject: (object: fabric.Object) => {
          fabricCanvasRef.current?.remove(object);
          requestRender();
        },
        clear: () => {
          fabricCanvasRef.current?.clear();
          requestRender();
        },
      }),
      [requestRender, requestSmoothRender]
    );

    // Update canvas dimensions when props change (synchronously before paint)
    // This handles quality changes WITHOUT recreating the canvas
    useLayoutEffect(() => {
      if (!fabricCanvasRef.current) return;

      const canvas = fabricCanvasRef.current;

      // Disable auto-rendering during dimension update to avoid intermediate frames
      const wasRenderOnAddRemove = canvas.renderOnAddRemove;
      canvas.renderOnAddRemove = false;

      // Upgrade canvas backing store to recording quality
      upgradeFabricBackstoreToQuality(canvas, recordingQuality, canvasRatio, width, height);

      // Restore render behavior and render once
      canvas.renderOnAddRemove = wasRenderOnAddRemove;
      requestRender();
      
      // CRITICAL: After render, recalculate canvas offset and object coordinates
      // This fixes control hit detection issues after CSS resize
      // Use requestAnimationFrame to ensure DOM updates are complete
      requestAnimationFrame(() => {
        if (fabricCanvasRef.current) {
          const c = fabricCanvasRef.current as any;
          // Recalculate canvas element position in DOM
          if (typeof c.calcOffset === "function") {
            c.calcOffset();
          }
          // Update all object control coordinates with current zoom
          c.forEachObject((obj: fabric.Object) => {
            obj.setCoords();
          });
          c.requestRenderAll();
        }
      });
      
    }, [width, height, requestRender, recordingQuality, canvasRatio]);

    // Debug logging disabled to reduce console bloat

    return (
      <canvas
        ref={canvasRef}
        className={className}
        style={{
          width: `${width}px`,
          height: `${height}px`,
          display: "block",
          borderRadius: "10px",
          overflow: "visible",
        }}
      />
    );
  }
);

FabricCanvas.displayName = "FabricCanvas";

export default FabricCanvas;
